import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { fetchLectureData, fetchLecturesBySpeaker } from '../services/googleSheets';
import { useConfig } from '../config/ConfigContext';

/**
 * Sidebar component with hover menus
 * @returns {JSX.Element} Sidebar component
 */
const Sidebar = () => {
  const { config, theme, loading: configLoading, error: configError } = useConfig(); // Get theme too
  const location = useLocation();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState(null);
  const [speakers, setSpeakers] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [activeSpeaker, setActiveSpeaker] = useState(null);
  const [speakerLectures, setSpeakerLectures] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [eventLectures, setEventLectures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [error, setError] = useState(null);
  const sidebarRef = useRef(null);

  // Check if device is mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Update isMobile state when window is resized
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch lecture data on component mount or when config changes
  useEffect(() => {
    const loadLectures = async () => {
      // Wait for config
      if (configLoading || !config) {
        console.log('Sidebar: Waiting for config...');
        setLoading(true); // Keep loading state while waiting
        return; 
      }
      // Handle config error
      if (configError) {
        console.error('Sidebar: Config loading failed:', configError);
        setError(`Config Error: ${configError.message || configError}`);
        setLoading(false);
        return;
      }
      // Ensure google_sheet_id is available
      if (!config.google_sheet_id) {
        console.error('Sidebar: google_sheet_id is missing from config.');
        setError('Spreadsheet ID is missing in configuration.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null); // Clear previous errors
      try {
        console.log(`Sidebar: Fetching data for sheet ID: ${config.google_sheet_id}`);
        const data = await fetchLectureData(config.google_sheet_id); // Pass sheetId
        setLectures(data);
        
        // Extract unique speakers
        const uniqueSpeakers = [...new Set(data.map(lecture => lecture.speaker).filter(Boolean))];
        setSpeakers(uniqueSpeakers);
        
        // Extract unique events from the lecture data (column E in spreadsheet)
        const uniqueEvents = [...new Set(data
          .map(lecture => lecture.event)
          .filter(event => event && event.trim() !== ''))];
        
        // Sort events alphabetically
        const sortedEvents = [...uniqueEvents].sort((a, b) => a.localeCompare(b));
        
        setEvents(sortedEvents);
      } catch (error) {
        console.error('Error loading lectures:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadLectures();
  }, [config, configLoading, configError]); // Add dependencies

  // Handle speaker hover/click
  useEffect(() => {
    if (activeSpeaker) {
      const fetchSpeakerLectures = async () => {
        try {
          const speakerLectureData = await fetchLecturesBySpeaker(activeSpeaker);
          setSpeakerLectures(speakerLectureData);
        } catch (error) {
          console.error('Error fetching speaker lectures:', error);
        }
      };
      
      fetchSpeakerLectures();
    }
  }, [activeSpeaker]);

  // Handle event hover/click
  useEffect(() => {
    if (activeEvent) {
      const fetchEventLectures = async () => {
        try {
          const eventLectureData = lectures.filter(lecture => lecture.event === activeEvent);
          setEventLectures(eventLectureData);
        } catch (error) {
          console.error('Error fetching event lectures:', error);
        }
      };
      
      fetchEventLectures();
    }
  }, [activeEvent, lectures]);

  // Handle click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setActiveMenu(null);
        setActiveSpeaker(null);
        setActiveEvent(null);
        if (isMobile) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);

  // Handle mouse leave for the entire sidebar
  const handleSidebarMouseLeave = () => {
    if (isMobile) return; // Don't close menus on mobile when mouse leaves
    
    // Close all menus
    setActiveMenu(null);
    setActiveSpeaker(null);
    setActiveEvent(null);
  };

  // Handle menu hover
  const handleMenuHover = (menu) => {
    setActiveMenu(menu);
    
    // Reset submenu states when changing main menu
    if (menu !== 'speakers') {
      setActiveSpeaker(null);
      setSpeakerLectures([]);
    }
    
    if (menu !== 'events') {
      setActiveEvent(null);
      setEventLectures([]);
    }
  };

  // Handle menu mouse leave
  const handleMenuLeave = () => {
    if (!isMobile) {
      setActiveMenu(null);
      setActiveSpeaker(null);
      setActiveEvent(null);
    }
  };

  // Handle speaker hover
  const handleSpeakerHover = (speaker) => {
    if (isMobile) return; // Skip hover effects on mobile
    
    setActiveMenu('speakers');
    setActiveSpeaker(speaker);
    
    // Get lectures for this speaker
    const filtered = lectures.filter(lecture => lecture.speaker === speaker);
    setSpeakerLectures(filtered);
  };

  // Handle event hover
  const handleEventHover = (event) => {
    if (isMobile) return; // Skip hover effects on mobile
    
    setActiveMenu('events');
    setActiveEvent(event);
    
    // Get lectures for this event
    const filtered = lectures.filter(lecture => lecture.event === event);
    setEventLectures(filtered);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    
    // Reset active menus when closing mobile menu
    if (isMobileMenuOpen) {
      setActiveMenu(null);
      setActiveSpeaker(null);
      setActiveEvent(null);
    }
  };

  // Styles
  const sidebarStyle = {
    width: isMobile ? (isMobileMenuOpen ? '100%' : '0') : '250px',
    height: '100vh',
    backgroundColor: theme.colors.primary,
    color: 'white',
    position: 'fixed',
    left: 0,
    top: 0,
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.3s ease',
    overflow: isMobile ? 'auto' : 'visible',
  };

  const mobileMenuButtonStyle = {
    display: isMobile ? 'block' : 'none',
    position: 'fixed',
    top: '10px',
    left: '10px',
    zIndex: 1001, // Increased z-index to ensure it's visible above other elements
    backgroundColor: theme.colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '20px',
  };

  const logoContainerStyle = {
    padding: '30px 20px',
    textAlign: 'center',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    marginBottom: '20px',
    display: isMobile && !isMobileMenuOpen ? 'none' : 'block',
  };

  const logoStyle = {
    textDecoration: 'none',
    display: 'block',
    cursor: 'pointer',
  };

  const menuItemStyle = (isActive) => ({
    padding: '15px 20px',
    cursor: 'pointer',
    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
    transition: 'background-color 0.3s',
    fontSize: '18px',
    position: 'relative',
    fontWeight: isActive ? 'bold' : 'normal',
    fontFamily: theme.fonts.menu,
    display: isMobile && !isMobileMenuOpen ? 'none' : 'block',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  });

  const hoverMenuStyle = {
    position: 'fixed',
    left: isMobile ? '0' : '250px',
    top: '0',
    width: isMobile ? '100%' : '250px',
    backgroundColor: 'white',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    zIndex: 999, // Increased z-index to ensure visibility
    height: '100vh',
    overflowY: 'auto',
    borderLeft: '1px solid #f0f0f0',
  };

  const hoverMenuItemStyle = {
    padding: '12px 20px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    color: '#333333', // Always dark — these panels have white backgrounds
    transition: 'background-color 0.3s',
    position: 'relative',
    fontFamily: theme.fonts.body,
  };

  const activeHoverMenuItemStyle = {
    ...hoverMenuItemStyle,
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
  };

  const subMenuStyle = {
    position: 'fixed',
    left: isMobile ? '0' : '500px',
    top: '0',
    width: isMobile ? '100%' : '300px',
    backgroundColor: 'white',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    zIndex: 1000, // Increased z-index to ensure proper layering
    height: '100vh',
    overflowY: 'auto',
    borderLeft: '1px solid #f0f0f0',
  };

  const subMenuItemStyle = {
    padding: '12px 20px',
    cursor: 'pointer',
    borderBottom: '1px solid #f0f0f0',
    color: '#333333', // Always dark — sub-menus have white backgrounds
    fontFamily: theme.fonts.body,
  };

  const loadingStyle = {
    padding: '20px',
    textAlign: 'center',
    color: '#666666', // Always dark — loading appears on white panels
  };

  // -- Determine Logo Source --
  // Support both new and legacy config field structures
  const organization = config?.organization || {};

  // Determine logo source in order of priority
  const logoSrc =
    organization.logo || // legacy nested
    config.logo_url || // v3 schema
    config.logo || // v2 schema
    '/images/default-logo.png'; // fallback

  // -- Determine Center Name (for alt text, potentially) --
  const centerName =
    organization.name || // legacy nested
    config.name || // v3 schema
    'Center Name'; // fallback

  // Handle config loading state
  if (configLoading) {
    return (
      <div style={sidebarStyle}>
        <p>Loading configuration...</p>
      </div>
    );
  }

  // Handle config error state
  if (configError || !config) {
    return (
      <div style={sidebarStyle}>
        <p>Error loading configuration: {configError ? (configError.message || configError) : 'Config not available.'}</p>
      </div>
    );
  }

  // Handle data loading state
  if (loading) {
    return (
      <div style={sidebarStyle}>
        <p>Loading...</p>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div style={sidebarStyle}>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && (
        <button style={mobileMenuButtonStyle} onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? '✕' : '☰'}
        </button>
      )}

      <div 
        ref={sidebarRef} 
        style={sidebarStyle} 
        onMouseLeave={handleSidebarMouseLeave}
      >
        <div style={logoContainerStyle}>
          <Link to="/" style={logoStyle} onClick={() => isMobile && setIsMobileMenuOpen(false)}>
            <img
              src={logoSrc}
              alt={`${centerName} Logo`}
              style={{
                width: '200px',
                height: 'auto',
                margin: '0 auto',
                borderRadius: '50%',
                backgroundColor: 'white'
              }}
            />
          </Link>
        </div>

        {/* Main Navigation */}
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: isMobile && !isMobileMenuOpen ? 'none' : 'block' }}>
          {/* Home Link */}
          <li style={menuItemStyle(location.pathname === '/')}>
            <Link 
              to="/"
              style={{ color: 'white', textDecoration: 'none', display: 'block' }} 
              onClick={() => isMobile && setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
          </li>
          
          {/* Events Menu */}
          <li
            style={menuItemStyle(activeMenu === 'events')}
            onMouseEnter={() => handleMenuHover('events')}
            onClick={() => isMobile && handleMenuHover('events')}
          >
            Events {isMobile && (activeMenu === 'events' ? '▲' : '▼')}
          </li>
          
          {/* Speakers Menu */}
          <li 
            style={menuItemStyle(activeMenu === 'speakers')}
            onMouseEnter={() => handleMenuHover('speakers')}
            onClick={() => isMobile && handleMenuHover('speakers')}
          >
            Speakers {isMobile && (activeMenu === 'speakers' ? '▲' : '▼')}
          </li>
          
          {/* About Link - Conditional */}
          {config?.about_us_enabled && (
            <li style={menuItemStyle(location.pathname === '/about')}>
              <Link 
                to="/about"
                style={{ color: 'white', textDecoration: 'none', display: 'block' }} 
                onClick={() => isMobile && setIsMobileMenuOpen(false)}
              >
                About
              </Link>
            </li>
          )}
          
          {/* Support Link - Conditional */}
          {config?.support_enabled && (
            <li style={menuItemStyle(location.pathname === '/support')}>
              <Link 
                to="/support"
                style={{ color: 'white', textDecoration: 'none', display: 'block' }} 
                onClick={() => isMobile && setIsMobileMenuOpen(false)}
              >
                Support
              </Link>
            </li>
          )}
        </ul>

        {/* Hover Menus (Desktop) or Submenus (Mobile) */}
        {(activeMenu || (isMobile && isMobileMenuOpen)) && (
          <div 
            style={activeMenu ? hoverMenuStyle : {}}
          >
            {/* Lectures Menu */}
            {activeMenu === 'lectures' && (
              <div 
                style={hoverMenuStyle}
                onMouseEnter={() => handleMenuHover('lectures')}
              >
                <h3 style={{ padding: '15px 20px', margin: 0, borderBottom: '1px solid #f0f0f0', fontFamily: theme.fonts.heading }}>
                  All Lectures
                </h3>
                <Link
                  to="/lectures/search"
                  style={{ textDecoration: 'none' }}
                  onClick={() => isMobile && setIsMobileMenuOpen(false)}
                >
                  <div style={{ ...hoverMenuItemStyle, background: '#f5f7fa', fontWeight: 500 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                      Search Lectures
                    </div>
                  </div>
                </Link>
                {lectures.map((lecture, index) => (
                  <Link 
                    key={index} 
                    to={`/lecture/${lecture.lectureId}`} 
                    style={{ textDecoration: 'none' }}
                    onClick={() => isMobile && setIsMobileMenuOpen(false)}
                  >
                    <div style={hoverMenuItemStyle}>
                      <div><strong>{lecture.title}</strong></div>
                      <div style={{ fontSize: '0.8em', color: theme.colors.darkGray }}>
                        ID: {lecture.lectureId} | {lecture.event}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            
            {/* Speakers Menu */}
            {activeMenu === 'speakers' && (
              <div 
                style={hoverMenuStyle}
                onMouseEnter={() => handleMenuHover('speakers')}
              >
                <h3 style={{ padding: '15px 20px', margin: 0, borderBottom: '1px solid #f0f0f0', fontFamily: theme.fonts.heading }}>
                  All Speakers
                </h3>
                {speakers.map((speaker, index) => (
                  <div 
                    key={index} 
                    style={activeSpeaker === speaker ? activeHoverMenuItemStyle : hoverMenuItemStyle}
                    onMouseEnter={() => !isMobile && handleSpeakerHover(speaker)}
                    onClick={() => isMobile && handleSpeakerHover(activeSpeaker === speaker ? null : speaker)}
                  >
                    {speaker}
                    
                    {/* Speaker's Lectures Submenu */}
                    {activeSpeaker === speaker && (
                      <div 
                        style={subMenuStyle}
                        onMouseEnter={() => !isMobile && handleSpeakerHover(speaker)}
                      >
                        <h3 style={{ padding: '15px 20px', margin: 0, borderBottom: '1px solid #f0f0f0', fontFamily: theme.fonts.heading }}>
                          Lectures by {speaker}
                        </h3>
                        {speakerLectures.length === 0 ? (
                          <div style={loadingStyle}>Loading lectures...</div>
                        ) : (
                          speakerLectures.map((lecture, idx) => (
                            <Link 
                              key={idx} 
                              to={`/lecture/${lecture.lectureId}`} 
                              style={{ textDecoration: 'none' }}
                              onClick={() => isMobile && setIsMobileMenuOpen(false)}
                            >
                              <div style={subMenuItemStyle}>
                                <div><strong>{lecture.title}</strong></div>
                                <div style={{ fontSize: '0.8em', color: theme.colors.darkGray }}>
                                  ID: {lecture.lectureId} | {lecture.event}
                                </div>
                              </div>
                            </Link>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Events Menu */}
            {activeMenu === 'events' && (
              <div 
                style={hoverMenuStyle}
                onMouseEnter={() => handleMenuHover('events')}
              >
                <h3 style={{ padding: '15px 20px', margin: 0, borderBottom: '1px solid #f0f0f0', fontFamily: theme.fonts.heading }}>
                  All Events
                </h3>
                {events.map((event, index) => (
                  <div 
                    key={index} 
                    style={activeEvent === event ? activeHoverMenuItemStyle : hoverMenuItemStyle}
                    onMouseEnter={() => !isMobile && handleEventHover(event)}
                    onClick={() => isMobile && handleEventHover(activeEvent === event ? null : event)}
                  >
                    {event}
                    
                    {/* Event's Lectures Submenu */}
                    {activeEvent === event && (
                      <div 
                        style={subMenuStyle}
                        onMouseEnter={() => !isMobile && handleEventHover(event)}
                      >
                        <h3 style={{ padding: '15px 20px', margin: 0, borderBottom: '1px solid #f0f0f0', fontFamily: theme.fonts.heading }}>
                          Lectures from {event}
                        </h3>
                        <Link 
                          to={`/events/${encodeURIComponent(event)}`}
                          style={{ textDecoration: 'none' }}
                          onClick={() => isMobile && setIsMobileMenuOpen(false)}
                        >
                          <div style={{
                            ...subMenuItemStyle,
                            backgroundColor: theme.colors.lightGray,
                            fontWeight: 'bold',
                            color: theme.colors.primary
                          }}>
                            View All Lectures in {event}
                          </div>
                        </Link>
                        {eventLectures.length === 0 ? (
                          <div style={loadingStyle}>Loading lectures...</div>
                        ) : (
                          eventLectures.map((lecture, idx) => (
                            <Link 
                              key={idx} 
                              to={`/lecture/${lecture.lectureId}`} 
                              style={{ textDecoration: 'none' }}
                              onClick={() => isMobile && setIsMobileMenuOpen(false)}
                            >
                              <div style={subMenuItemStyle}>
                                <div><strong>{lecture.title}</strong></div>
                                <div style={{ fontSize: '0.8em', color: theme.colors.darkGray }}>
                                  ID: {lecture.lectureId} | Speaker: {lecture.speaker}
                                </div>
                              </div>
                            </Link>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
